// src/components/providerAssignments/CircleAssignmentsTab.tsx
//
// Assignments tab on the Circle detail page. Filterable list + actions.

import { useMemo, useState } from 'react';
import { useHasCapability } from '../../services/centralContext';
import {
  useGetProviderRolesQuery,
  useListProviderAssignmentsQuery,
} from '../../api/providerAssignmentsApi';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import type {
  AssignmentPriority,
  AssignmentStatus,
  Circle,
} from '../../types/api';
import {
  ASSIGNMENT_PRIORITIES,
  ASSIGNMENT_STATUSES,
} from '../../types/api';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  providerLabel,
} from './assignmentDisplay';
import AssignmentsTable from './AssignmentsTable';
import AssignmentWizard from './AssignmentWizard';

export interface CircleAssignmentsTabProps {
  circle: Circle;
  workspaceId?: string;
}

export default function CircleAssignmentsTab({
  circle,
  workspaceId,
}: CircleAssignmentsTabProps) {
  const canCreate = useHasCapability('assignments', 'create');
  const [showWizard, setShowWizard] = useState(false);
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<AssignmentPriority | ''>('');
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);

  const { data: teamMembers = [] } = useGetTeamMembersQuery(
    { workspaceId: workspaceId ?? '' },
    { skip: !workspaceId },
  );
  const { data: roles = [] } = useGetProviderRolesQuery();

  const { data: assignments = [], isLoading, isError, refetch } = useListProviderAssignmentsQuery(
    {
      workspaceId,
      departmentCircleId: circle.id,
      providerUserId: providerFilter || undefined,
      providerRoleId: roleFilter || undefined,
      priority: priorityFilter || undefined,
      status: activeOnly ? undefined : (statusFilter || undefined),
      activeOnly,
    },
    { skip: !workspaceId || !circle.id },
  );

  const rowCount = useMemo(() => assignments.length, [assignments]);

  return (
    <section data-testid="circle-assignments-tab" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
          aria-label="Filter by provider"
        >
          <option value="">All providers</option>
          {teamMembers.map((m) => (
            <option key={m.userId} value={m.userId}>
              {providerLabel({ fullName: m.fullName, email: m.email })}
            </option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
          aria-label="Filter by role"
        >
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter((e.target.value as AssignmentPriority) || '')}
          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
          aria-label="Filter by priority"
        >
          <option value="">All priorities</option>
          {ASSIGNMENT_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABELS[p]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active only
        </label>
        {!activeOnly && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value as AssignmentStatus) || '')}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {ASSIGNMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        )}
        <span className="ml-auto text-[11px] text-slate-500">{rowCount} assignments</span>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
            data-testid="circle-assignments-new"
          >
            New assignment
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-slate-500">Loading assignments…</p>}
      {isError && (
        <p className="text-xs text-red-600">
          Failed to load assignments. Try refreshing.
        </p>
      )}
      {!isLoading && !isError && (
        <AssignmentsTable
          rows={assignments}
          columns={[
            'person',
            'provider',
            'role',
            'priority',
            'status',
            'startDate',
            'endDate',
            'assignedBy',
          ]}
          onChanged={() => refetch()}
        />
      )}

      {showWizard && (
        <AssignmentWizard
          onClose={() => setShowWizard(false)}
          initial={{ departmentCircleId: circle.id }}
          onCreated={() => refetch()}
        />
      )}
    </section>
  );
}
